import * as React from 'react'
import { Button, ButtonProps } from '@amzn/dvui/dist/Button'
import { Text } from '@amzn/dvui/dist/Text'
import * as st from './DownloadButton.module.scss'
import { DownloadStatus } from '@shared/SPA/UWPNotifier'
import { camelToDataAttrs } from 'DVWebNodeUtils'
import cn from 'classnames'
import { AtfTreatmentProps } from '../../../widgets/DVWebNodeDetail/reducers/features'
import { isTreatment } from '../../../widgets/DVWebNodeDetail/utils/utils'
import * as utilityClasses from '../../../widgets/DVWebNodeDetail/components/utilityClasses.module.scss'

const radius = 11;
const circumference = Math.round(Math.PI * radius * 2)
const getOffset = (val = 0) => Math.round(((100 - Math.min(val, 100)) / 100) * circumference)

// TODO(AVWebDP-111): remove once we migrated deeplinking to Node
const isSupportedEdgeBrowser = typeof navigator !== 'undefined' && navigator.userAgent &&
  navigator.userAgent.indexOf('Edge') > -1 &&
  navigator.userAgent.indexOf('Windows NT 10.0') > -1

const deeplinkClass = global.__NATIVEAPP__ || isSupportedEdgeBrowser ? '' : 'js-deeplinkable'

type DownloadIcon = {
  status?: DownloadStatus
  progress?: number
  hasLabel: boolean
}

export const DownloadIcon = ({ status, progress = 0, hasLabel }: DownloadIcon) => {
  const baseColor = "#8197A4"; // $_elephant-gray
  const activeColor = "#F2F4F6"; // $_off-white
  const lineWidth = 2;
  const strokeDashoffset = status === DownloadStatus.WAITING ? getOffset(75) : getOffset(progress);
  const transition = `strokeDashoffset 2s linear`;

  const showCircle = status && [
    DownloadStatus.DOWNLOADING,
    DownloadStatus.SEASON_DOWNLOADING,
    DownloadStatus.PAUSED,
    DownloadStatus.QUEUED,
    DownloadStatus.ERROR,
    DownloadStatus.SEASON_ERROR,
  ].includes(status)

  const showProgress = [
    DownloadStatus.DOWNLOADING,
    DownloadStatus.PAUSED,
    DownloadStatus.SEASON_DOWNLOADING,
  ].includes(status)

  const showArrow = [
    DownloadStatus.IDLE,
    DownloadStatus.PAUSED,
    DownloadStatus.QUEUED,
    DownloadStatus.SEASON_IDLE,
    DownloadStatus.REMOVED,
    DownloadStatus.CANCELLED
  ].includes(status)

  const showtray = [
    DownloadStatus.IDLE,
    DownloadStatus.SEASON_IDLE,
    DownloadStatus.REMOVED,
    DownloadStatus.CANCELLED
  ].includes(status)
  
  const showDots = (status === DownloadStatus.COMPLETED && !hasLabel) || (status === DownloadStatus.DISABLED)
  const showSpinner = status === DownloadStatus.WAITING
  return (
    <svg height="24" width="24" xmlns="http://www.w3.org/2000/svg" className={cn({[st.spin]: showSpinner})} >
      <g fillRule="nonzero" fill="none">
        {showtray && (
          <path
            className={st.stroke}
            stroke={baseColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.997 18.087v4.972h18.03v-4.972"
          />
        )}

        {showArrow && (
          <path
            className={st.stroke}
            d="M12 5v12M7.5 12.5L12 17M16.5 12.5L12 17"
            stroke={status === DownloadStatus.PAUSED ? activeColor : baseColor}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        )}

        {showCircle && (
          <circle
            className={st.circle}
            stroke={baseColor}
            strokeWidth={lineWidth}
            fill="none"
            cx="12"
            cy="12"
            r="11"
          />
        )}

        {(showProgress || showSpinner) && (
          <circle
            stroke={activeColor}
            transform="rotate(-90 12 12)"
            strokeWidth={lineWidth}
            fill="none"
            cx="12"
            cy="12"
            r="11"
            strokeDasharray="69"
            strokeDashoffset="69"
            strokeLinecap={'butt'}
            style={{ strokeDashoffset, transition }}
          />
        )}
        {showDots && (
          <g fill={baseColor}>
            <path d="M 14.121094 0.878906 C 15.292969 2.050781 15.292969 3.949219 14.121094 5.121094 C 12.949219 6.292969 11.050781 6.292969 9.878906 5.121094 C 8.707031 3.949219 8.707031 2.050781 9.878906 0.878906 C 11.050781 -0.292969 12.949219 -0.292969 14.121094 0.878906 M 14.121094 9.878906 C 15.292969 11.050781 15.292969 12.949219 14.121094 14.121094 C 12.949219 15.292969 11.050781 15.292969 9.878906 14.121094 C 8.707031 12.949219 8.707031 11.050781 9.878906 9.878906 C 11.050781 8.707031 12.949219 8.707031 14.121094 9.878906 M 14.121094 18.878906 C 15.292969 20.050781 15.292969 21.949219 14.121094 23.121094 C 12.949219 24.292969 11.050781 24.292969 9.878906 23.121094 C 8.707031 21.949219 8.707031 20.050781 9.878906 18.878906 C 11.050781 17.707031 12.949219 17.707031 14.121094 18.878906 "/>
          </g>
        )}
        {(status === DownloadStatus.COMPLETED && hasLabel) && (
          <g fill={baseColor}><path d="M15 18V8a1 1 0 012 0v11a1 1 0 01-1 1H4a1 1 0 01-1-1V8a1 1 0 112 0v10h10z"/><path d="M7 8a1 1 0 112 0v7a1 1 0 01-2 0V8zm4 0a1 1 0 012 0v7a1 1 0 01-2 0V8zM1 5a1 1 0 110-2h18a1 1 0 010 2H1z"/><path d="M6.8 2v2c0 .552-.403 1-.9 1S5 4.552 5 4V1c0-.552.403-1 .9-1h7.2c.497 0 .9.448.9 1v3c0 .552-.403 1-.9 1s-.9-.448-.9-1V2H6.8z"/></g>
        )}
        {(status === DownloadStatus.DOWNLOADING || status === DownloadStatus.SEASON_DOWNLOADING) && (
          <g fill={activeColor}>
            <path d="M10.3 16.012c0 .828-.335 1.054-.75 1.054-.414 0-.75-.226-.75-1.054V7.007C8.8 6.178 9.158 6 9.55 6s.75.178.75 1.007v9.005zM15.3 16.012c0 .828-.335 1.054-.75 1.054s-.75-.226-.75-1.054V7.007c0-.829.358-1.007.75-1.007s.75.178.75 1.007v9.005z" />
          </g>
        )}
        {(status === DownloadStatus.ERROR || status === DownloadStatus.SEASON_ERROR) && (
          <g fill={baseColor}>
            <path d="M13 13.024a.978.978 0 0 1-.975.976h-.05a.978.978 0 0 1-.975-.976V6.976c0-.537.439-.976.976-.976h.049c.536 0 .975.439.975.976v6.048zM11 16h2v2.04h-2z" />
          </g>
        )}
      </g>
    </svg>
  )
}

export type DownloadLabels = { [key in keyof typeof DownloadStatus]: string }

interface DownloadButtonProps {
  label: string
  downloadURL: string
  fallbackURL?: string
  progress?: number
  status?: DownloadStatus
  onClick?: (event: React.MouseEvent) => void
  isDropList?: boolean
  showIcon?: boolean
}

export const DownloadButton = ({ progress, status = DownloadStatus.IDLE, downloadURL, fallbackURL, onClick, isDropList }: DownloadButtonProps) => {
  const dataAttrs = camelToDataAttrs({ fallbackUrl: fallbackURL })
  const Tag = isDropList ? 'span' : 'a'
  const tagProps = {
    onClick,
    ...(!isDropList && { href: downloadURL }),
    ...dataAttrs
  }
  return (
    <Tag className={`${deeplinkClass} ${st.button}`} {...tagProps}>
      <DownloadIcon hasLabel={false} {...{ progress, status }} />
    </Tag>
  )
}

type DownloadButtonWithLabelProps = DownloadButtonProps & ButtonProps & AtfTreatmentProps

export const DownloadButtonWithLabel = ({ onClick, progress,
  status, label, downloadURL, fallbackURL, forceDoubleHeight,
  atfTreatment, showIcon = true }: DownloadButtonWithLabelProps) => {
  const dataAttrs = camelToDataAttrs({ fallbackUrl: fallbackURL })
  const props = {
    ...dataAttrs,
    onClick,
    forceDoubleHeight,
    href: downloadURL,
    className: cn(deeplinkClass, st.withLabelButton, {[cn(utilityClasses.fullWidthPhablet, st.downloadButton)]: isTreatment(atfTreatment, 'T1', 'T2', 'T3')}),
  }

  const atfTreatmentProps: ButtonProps = {
    ...(isTreatment(atfTreatment,'T1', 'T2', 'T3') && { forceDoubleHeight: false }),
    ...(isTreatment(atfTreatment,'T1', 'T3') && { shape: 'pill' })
  }

  return (
    <Button {...props} {...atfTreatmentProps}>
      {showIcon && <span className={st.buttonIcon}>
        <DownloadIcon hasLabel={true} {...{ progress, status }} />
      </span>}
      <Text tag="span" string={label} className={isTreatment(atfTreatment, 'T1', 'T2', 'T3') ? st.downloadButtonText : undefined}/>
    </Button>
  )
}
